<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import logo from '@/assets/img/logo_smilepicture.png'
import { fetchServicesData, fetchContactData } from '@/services/api'


const props = defineProps({
    phoneNumber: {
        type: String,
        default: ''
    }
})
const isScrolled = ref(false)
const route = useRoute()
// Services data from API
const services = ref([])
const servicesLoading = ref(false)

const handleScroll = () => {
    isScrolled.value = window.scrollY > 50
}

const initOffcanvas = () => {
    if (!window.$) return

    // clean previous handlers
    window.$('[data-toggle="offcanvas-nav"]').off('click')
    window.$('.offcanvas-nav-close').off('click')
    window.$('.offcanvas-nav .nav-link').off('click')

    // open / close offcanvas
    window.$('[data-toggle="offcanvas-nav"]').on('click', function (e) {
        e.preventDefault()
        window.$('.offcanvas-nav').toggleClass('open')
        window.$('body').toggleClass('offcanvas-nav-active')
    })

    // close button
    window.$('.offcanvas-nav-close').on('click', function () {
        window.$('.offcanvas-nav').removeClass('open')
        window.$('body').removeClass('offcanvas-nav-active')
    })

    // any link click → close (route watcher will handle SPA navigation)
    window.$('.offcanvas-nav .nav-link').on('click', function () {
        window.$('.offcanvas-nav').removeClass('open')
        window.$('body').removeClass('offcanvas-nav-active')
    })
}

// Slugify function to convert service title to URL-friendly format
const slugify = (title) => {
    return title
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
}

// Get service route path
const getServicePath = (service) => {
    const slug = service.slug || slugify(service.title)
    return `/services/${slug}`
}

// Fetch services from API
const loadServices = async () => {
    servicesLoading.value = true
    try {
        const response = await fetchServicesData(false)
        services.value = response.services || []
    } catch (error) {
        console.error('Failed to load services:', error)
        // Fallback to default services if API fails
        services.value = []
    } finally {
        servicesLoading.value = false
    }
}

onMounted(async () => {
    window.addEventListener('scroll', handleScroll)
    await nextTick()
    initOffcanvas()
    loadServices()
})

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
})

/* IMPORTANT:
   close offcanvas every time route changes (Services & submenu fix) */
watch(
    () => route.fullPath,
    () => {
        if (window.$) {
            window.$('.offcanvas-nav').removeClass('open')
            window.$('body').removeClass('offcanvas-nav-active')
        }
    }
)

const isActive = (href) => {
    if (href === '/services') {
        return route.path.startsWith('/services')
    }
    return route.path === href
}

const isServiceActive = (service) => {
    const servicePath = getServicePath(service)
    return route.path === servicePath
}
</script>

<template>
    <header class="wrapper bg-black">
        <nav class="navbar navbar-expand-lg center-nav navbar-dark" :class="{ 'navbar-fixed': isScrolled }">
            <div class="container flex-lg-row flex-nowrap align-items-center">

                <!-- BRAND -->
                <div class="navbar-brand w-100">
                    <RouterLink to="/">
                        <img class="logo-dark" :src="logo" alt="">
                        <img class="logo-light" :src="logo" alt="">
                    </RouterLink>
                </div>

                <!-- MENU -->
                <div class="navbar-collapse offcanvas-nav">
                    <div
                        class="offcanvas-header d-lg-none d-flex align-items-center justify-content-between ps-0 pe-4 py-4">
                        <RouterLink to="/" class="offcanvas-logo">
                            <img :src="logo" alt="">
                        </RouterLink>

                        <button type="button" class="btn-close btn-close-white offcanvas-nav-close" aria-label="Close">
                        </button>
                    </div>

                    <ul class="navbar-nav">
                        <li class="nav-item">
                            <RouterLink to="/" class="nav-link" :class="{ active: isActive('/') }">
                                Home
                            </RouterLink>
                        </li>

                        <li class="nav-item">
                            <RouterLink to="/company" class="nav-link" :class="{ active: isActive('/company') }">
                                Company
                            </RouterLink>
                        </li>

                        <li class="nav-item">
                            <RouterLink to="/works" class="nav-link" :class="{ active: isActive('/works') }">
                                Works
                            </RouterLink>
                        </li>

                        <!-- SERVICES (Dynamic from API) -->
                        <li class="nav-item dropdown">
                            <RouterLink to="/services" class="nav-link has-submenu"
                                :class="{ active: isActive('/services') }">
                                Services <span class="sub-arrow"></span>
                            </RouterLink>

                            <ul class="dropdown-menu">
                                <li v-for="service in services" :key="service.id">
                                    <RouterLink class="dropdown-item" :to="getServicePath(service)"
                                        :class="{ active: isServiceActive(service) }">
                                        {{ service.title }}
                                    </RouterLink>
                                </li>
                            </ul>


                        </li>

                        <li class="nav-item">
                            <RouterLink to="/contact" class="nav-link" :class="{ active: isActive('/contact') }">
                                Contact
                            </RouterLink>
                        </li>
                    </ul>
                </div>

                <!-- RIGHT -->
                <div class="navbar-other w-100 d-flex ms-auto">
                    <ul class="navbar-nav flex-row align-items-center ms-auto">
                        <li class="nav-item d-none d-md-block">
                            <a :href="`https://wa.me/${phoneNumber}`" target="_blank"
                                class="btn btn-sm btn-secondary rounded-pill" style="color:#820358">
                                Consultation Now
                            </a>
                        </li>

                        <li class="nav-item d-lg-none">
                            <button class="hamburger animate plain" aria-label="Menu" data-toggle="offcanvas-nav">
                                <span></span>
                            </button>
                        </li>
                    </ul>
                </div>

            </div>
        </nav>
    </header>
</template>

<style>
.logo-light,
.logo-dark {
    height: 1.5rem;
}

/* sticky */
.navbar-fixed {
    position: fixed;
    top: 0;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(6px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    animation: slideDown .35s ease forwards;
    z-index: 1030;
}

/* MOBILE OFFCANVAS */
@media (max-width: 991.98px) {
    .offcanvas-nav {
        position: fixed;
        inset: 0;
        width: 100vw;
        height: 100vh;
        background-color: #000;
        z-index: 1050;
        overflow-y: auto;
        transform: translateX(-100%);
        transition: transform .3s ease;
    }

    .offcanvas-nav.open {
        transform: translateX(0);
    }

    body.offcanvas-nav-active {
        overflow: hidden;
    }

    .offcanvas-header img {
        max-width: 100%;
        height: 1.5rem;
    }

    /* SUBMENU ALWAYS OPEN ON MOBILE */
    .offcanvas-nav .dropdown-menu {
        position: static;
        display: block;
        background: transparent;
        padding-left: 1rem;
        margin-top: .5rem;
    }

    .offcanvas-nav .sub-arrow {
        display: none;
    }
}

/* DESKTOP */
@media (min-width: 992px) {
    .offcanvas-nav {
        position: relative;
        transform: none !important;
        background: transparent;
    }
}

@keyframes slideDown {
    from {
        transform: translateY(-100%);
    }

    to {
        transform: translateY(0);
    }
}

/* Dropdown item active state */
.dropdown-item.active {
    color: #ffae0b !important;
    background-color: transparent;
}

.dropdown-item:hover {
    color: #ffae0b;
}
</style>
